/**
 * ConnectionStatusSection Component
 *
 * Displays saved connections with their status in a collapsible section.
 * Uses compact card layout with responsive grid.
 * Shows all connected connections + up to 9 disconnected connections.
 * "More connections" button only appears when there are >9 disconnected connections.
 */

import { useCallback, useMemo, useState } from 'react';
import { ChevronDown, Plus, Server, MoreHorizontal, Plug, FolderOpen } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { cn } from '../../../../lib/utils';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { useConnectionsUIStore } from '../../../connections/store/uiStore';
import { ConnectionCardCompact } from '../../../connections/components/ConnectionCardCompact';
import { NewConnectionPopup } from '../../../connections/components/NewConnectionPopup';
import { QuickActionsBar } from './QuickActionsBar';
import { computeAllConnectionStatuses } from '../utils/connectionStatus';
import { sortConnectionsByDisplayOrder, getVisibleConnections } from '../../../connections/utils/connectionDisplayOrder';
import {
  openNewQueryTab,
  openTableExplorerFromStartPage,
  openSchemaOverviewFromStartPage,
  openDashboardsFromStartPage,
} from '../utils/quickActions';
import { ConnectionsEmptyState } from './ConnectionsEmptyState';
import { AnimatedCollapse } from './AnimatedCollapse';
import { isBrowserMode } from '../../../browser-mode';
import { useBrowserModeStore } from '../../../browser-mode/store/browserModeStore';
import { useConnectionInfoDialogStore } from '../../../connections/store/connectionInfoDialogStore';
import { useSessionStore } from '../../../sessions/store/sessionStore';
import { ConnectionInfoDialog } from '../../../connections/components/ConnectionInfoDialog';
import type { Connection, SaveConnectionOptions } from '../../../../types/electron';

type ConnectionStatusSectionProps = {
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
};

/** Maximum disconnected connections to show before "More" button */
const MAX_DISCONNECTED_CONNECTIONS = 9;

export function ConnectionStatusSection({
  isExpanded,
  onExpandedChange,
}: ConnectionStatusSectionProps) {
  // Connection store state
  const connections = useConnectionStore((state) => state.connections);
  const activeConnections = useConnectionStore((state) => state.activeConnections);
  const connectionOperationById = useConnectionStore(
    (state) => state.connectionOperationById
  );
  const connectionErrorById = useConnectionStore(
    (state) => state.connectionErrorById
  );
  const connectToDatabase = useConnectionStore((state) => state.connectToDatabase);
  const disconnectFromDatabase = useConnectionStore(
    (state) => state.disconnectFromDatabase
  );
  const updateConnection = useConnectionStore((state) => state.updateConnection);
  const deleteConnection = useConnectionStore((state) => state.deleteConnection);

  // Edit connection state
  const [editingConnection, setEditingConnection] = useState<Connection | null>(null);

  // UI store for dialogs
  const setShowConnectionsDialog = useConnectionsUIStore(
    (state) => state.setShowConnectionsDialog
  );

  const requestAutoOpenFilePicker = useBrowserModeStore(
    (state) => state.requestAutoOpenFilePicker
  );

  // Connection info dialog
  const openConnectionInfoDialog = useConnectionInfoDialogStore(
    (state) => state.openDialog
  );

  // Get active connection IDs for sorting
  const activeConnectionIds = useMemo(
    () => new Set(activeConnections.map((ac) => ac.id)),
    [activeConnections]
  );

  // Sort connections: active first, then by lastConnectedAt
  const sortedConnections = useMemo(
    () => sortConnectionsByDisplayOrder(connections, activeConnectionIds),
    [connections, activeConnectionIds]
  );

  // Get visible subset (all connected + up to 9 disconnected)
  const { visible: visibleConnections, remainingCount, hasMore } = useMemo(
    () => getVisibleConnections(sortedConnections, activeConnectionIds, MAX_DISCONNECTED_CONNECTIONS),
    [sortedConnections, activeConnectionIds]
  );

  // Compute connection statuses for visible connections only
  const connectionStatuses = useMemo(
    () =>
      computeAllConnectionStatuses(
        visibleConnections,
        activeConnections,
        connectionOperationById,
        connectionErrorById
      ),
    [visibleConnections, activeConnections, connectionOperationById, connectionErrorById]
  );

  // Handlers
  const handleToggleExpanded = useCallback(() => {
    onExpandedChange(!isExpanded);
  }, [isExpanded, onExpandedChange]);

  const handleConnect = useCallback(
    (connectionId: string) => {
      const connection = connections.find((c) => c.id === connectionId);
      if (connection) {
        connectToDatabase(connection);
      }
    },
    [connections, connectToDatabase]
  );

  const handleDisconnect = useCallback(
    async (connectionId: string) => {
      const success = await disconnectFromDatabase(connectionId);
      if (success) {
        // Explicit user intent: stop carrying this connection in the session.
        // Recorded only AFTER the disconnect resolves, so a save racing the
        // in-flight disconnect can't re-add it from `activeConnections`.
        useSessionStore.getState().removeConnectionFromSession(connectionId);
      }
    },
    [disconnectFromDatabase]
  );

  const handleAddConnection = useCallback(() => {
    setShowConnectionsDialog(true);
  }, [setShowConnectionsDialog]);

  const handleLoadSqliteFile = useCallback(() => {
    requestAutoOpenFilePicker();
    setShowConnectionsDialog(true);
  }, [requestAutoOpenFilePicker, setShowConnectionsDialog]);

  const handleMoreConnections = useCallback(() => {
    setShowConnectionsDialog(true);
  }, [setShowConnectionsDialog]);

  // Edit connection handlers
  const handleEdit = useCallback((connection: Connection) => {
    setEditingConnection(connection);
  }, []);

  const handleSaveEdit = useCallback(async (connection: Connection, options: SaveConnectionOptions): Promise<boolean> => {
    const wasConnected = activeConnectionIds.has(connection.id);

    // If connection was active, disconnect first
    if (wasConnected) {
      await disconnectFromDatabase(connection.id);
    }

    const success = await updateConnection(connection, options);
    if (success) {
      setEditingConnection(null);

      // If it was connected before, reconnect with updated settings
      if (wasConnected) {
        await connectToDatabase(connection);
      }
    }
    return success;
  }, [updateConnection, activeConnectionIds, disconnectFromDatabase, connectToDatabase]);

  const handleDeleteConnection = useCallback(async (connectionId: string): Promise<boolean> => {
    const success = await deleteConnection(connectionId);
    if (success) {
      setEditingConnection(null);
    }
    return success;
  }, [deleteConnection]);

  const handleCancelEdit = useCallback(() => {
    setEditingConnection(null);
  }, []);

  // Quick action handlers
  const handleNewQuery = useCallback(
    (connectionId: string, database?: string) => {
      openNewQueryTab(connectionId, database);
    },
    []
  );

  const handleTableExplorer = useCallback(
    (connectionId: string, database: string) => {
      openTableExplorerFromStartPage(connectionId, database);
    },
    []
  );

  const handleSchemaOverview = useCallback(
    (connectionId: string, database: string) => {
      openSchemaOverviewFromStartPage(connectionId, database);
    },
    []
  );

  const handleOpenDashboards = useCallback(() => {
    openDashboardsFromStartPage();
  }, []);

  const handleConnectionInfo = useCallback((connection: Connection) => {
    openConnectionInfoDialog(connection);
  }, [openConnectionInfoDialog]);

  // Stats for header badge
  const connectedCount = useMemo(
    () => activeConnections.length,
    [activeConnections]
  );

  return (
    <>
    <div
      className="rounded-lg border bg-surface-raised shadow-sm"
      data-testid="connection-status-section"
    >
      {/* Section Header */}
      <Button
        variant="ghost"
        className={cn(
          'w-full flex items-center justify-between px-4 py-3 h-auto',
          isExpanded && 'rounded-b-none'
        )}
        onClick={handleToggleExpanded}
        data-testid="connection-section-toggle"
      >
        <div className="flex items-center gap-2">
          <Server className="h-4 w-4" />
          <span className="text-sm font-medium">Connections</span>
          {connections.length > 0 && (
            <span className="text-xs text-muted-foreground tabular-nums">
              ({connectedCount} of {connections.length} connected)
            </span>
          )}
        </div>
        <ChevronDown
          className={cn('h-4 w-4 transition-transform duration-200', !isExpanded && '-rotate-90')}
        />
      </Button>

      {/* Section Content */}
      <AnimatedCollapse isOpen={isExpanded}>
        <div className="p-4 pt-2 @container" data-testid="connection-section-content">
          {/* Connection Cards Grid */}
          {connectionStatuses.length > 0 ? (
            (() => {
              const connectedCards = connectionStatuses.filter(cs => cs.status === 'connected');
              const disconnectedCards = connectionStatuses.filter(cs => cs.status !== 'connected');

              // Group disconnected cards into stacks of 3
              const disconnectedStacks: typeof disconnectedCards[] = [];
              for (let i = 0; i < disconnectedCards.length; i += 3) {
                disconnectedStacks.push(disconnectedCards.slice(i, i + 3));
              }

              return (
                <div className="grid grid-cols-1 @xl:grid-cols-2 @3xl:grid-cols-3 gap-3 mb-4">
                  {/* Connected cards - full size grid items */}
                  {connectedCards.map((cs) => (
                    <ConnectionCardCompact
                      key={cs.connection.id}
                      connection={cs.connection}
                      status={cs.status}
                      database={cs.database}
                      errorMessage={cs.errorMessage}
                      tunnelState={cs.tunnelState}
                      onConnect={() => handleConnect(cs.connection.id)}
                      onDisconnect={() => handleDisconnect(cs.connection.id)}
                      onEdit={() => handleEdit(cs.connection)}
                      onConnectionInfo={() => handleConnectionInfo(cs.connection)}
                      quickActions={
                        <QuickActionsBar
                          connectionStatus={cs}
                          onNewQuery={handleNewQuery}
                          onTableExplorer={handleTableExplorer}
                          onSchemaOverview={handleSchemaOverview}
                          onOpenDashboards={handleOpenDashboards}
                          onConnectionInfo={handleConnectionInfo}
                        />
                      }
                    />
                  ))}

                  {/* Disconnected cards - stacked 3 per grid cell */}
                  {disconnectedStacks.map((stack, stackIndex) => (
                    <div key={`stack-${stackIndex}`} className="flex flex-col gap-2 justify-start">
                      {stack.map((cs) => (
                        <ConnectionCardCompact
                          key={cs.connection.id}
                          connection={cs.connection}
                          status={cs.status}
                          database={cs.database}
                          errorMessage={cs.errorMessage}
                          tunnelState={cs.tunnelState}
                          onConnect={() => handleConnect(cs.connection.id)}
                          onDisconnect={() => handleDisconnect(cs.connection.id)}
                          onEdit={() => handleEdit(cs.connection)}
                          onConnectionInfo={() => handleConnectionInfo(cs.connection)}
                          compact
                        />
                      ))}
                    </div>
                  ))}
                </div>
              );
            })()
          ) : (
            isBrowserMode() ? (
              <div
                className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground"
                data-testid="connections-empty-state"
              >
                <Plug className="h-10 w-10 mb-3 opacity-30" aria-hidden="true" />
                <p className="text-sm font-medium">No connections yet</p>
                <p className="text-xs mt-1 max-w-[260px] text-pretty">
                  Load a SQLite database file to start exploring data.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 active:scale-[0.96] transition-[color,box-shadow,scale]"
                  onClick={handleLoadSqliteFile}
                  data-testid="load-sqlite-file-startpage-empty"
                >
                  <FolderOpen className="h-4 w-4 mr-2" />
                  Load your SQLite file
                </Button>
                <p className="text-xs mt-2 font-semibold text-muted-foreground">
                  Your file stays in your browser — nothing gets uploaded.
                </p>
              </div>
            ) : (
              <ConnectionsEmptyState onAddConnection={handleAddConnection} />
            )
          )}

          {/* Footer with More/Add buttons - only show when there are connections */}
          {connectionStatuses.length > 0 && (
            <div className="flex justify-between items-start">
              {/* More connections button (left) */}
              {hasMore ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMoreConnections}
                  data-testid="more-connections-button"
                  className="text-muted-foreground tabular-nums"
                >
                  <MoreHorizontal className="h-4 w-4 mr-2" />
                  {remainingCount} more connection{remainingCount !== 1 ? 's' : ''}...
                </Button>
              ) : (
                <div /> // Spacer
              )}

              {/* Add Connection / Load SQLite file (right) */}
              {isBrowserMode() ? (
                <div className="flex flex-col items-end gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="active:scale-[0.96] transition-[color,box-shadow,scale]"
                    onClick={handleLoadSqliteFile}
                    data-testid="load-sqlite-file-startpage"
                  >
                    <FolderOpen className="h-4 w-4 mr-2" />
                    Load your SQLite file
                  </Button>
                  <span
                    className="text-[11px] text-muted-foreground"
                    data-testid="browser-mode-desktop-cta"
                  >
                    Your file stays in your browser — nothing gets uploaded. Need{' '}
                    <span className="font-bold bg-foreground text-background px-1 rounded">PostgreSQL</span>
                    ,{' '}
                    <span className="font-bold bg-foreground text-background px-1 rounded">MS SQL</span>
                    {' '}or{' '}
                    <span className="font-bold bg-foreground text-background px-1 rounded">MySQL/MariaDB</span>
                    ?{' '}
                    <a
                      href="https://jamsql.com/?utm_source=app&utm_medium=referral&utm_campaign=start-page#download"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline font-medium"
                    >
                      Download the desktop app
                    </a>
                  </span>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="active:scale-[0.96] transition-[color,box-shadow,scale]"
                  onClick={handleAddConnection}
                  data-testid="add-connection-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Connection
                </Button>
              )}
            </div>
          )}
        </div>
      </AnimatedCollapse>
    </div>

    {/* Edit Connection Dialog */}
    {editingConnection && (
      <NewConnectionPopup
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
        onDelete={handleDeleteConnection}
        editConnection={editingConnection}
        isConnected={activeConnectionIds.has(editingConnection.id)}
      />
    )}

    {/* Connection Info Dialog */}
    <ConnectionInfoDialog />
    </>
  );
}
